import React, { useRef, useState, useCallback, useEffect } from 'react';

import { Button, ButtonTypes } from 'devextreme-react/button';
import { Toast } from 'devextreme-react/toast';
import { LoadPanel } from 'devextreme-react/load-panel';

import DatabaseSelectionForm, { DatabaseSelectionFormHandle } from '../database-selection/DatabaseSelectionForm';

import { formatMessage } from "devextreme/localization";

import DatabaseApi from "./../../api/DatabaseApi";
import DataSourceCharacteristicsApi from '../../api/DataSourceCharacteristicsApi';

import { JobStatusError } from '../../classes/consts/JobStatus';

import DatabaseSelectionDto from '../../classes/dtos/datastructuremodule/DatabaseSelectionDto';
import DataSourceCharacteristicsDto from '../../classes/dtos/datasourcemodule/DataSourceCharacteristicsDto';

interface DatabaseSelectionProps {
    DataSourceId: number;
    DatabaseReferenceId: number;
    DatabaseReferenceLabel: string;
    DatabaseTypeLabel: string;
    SetDatabaseSelected(databaseSelectionDto: DatabaseSelectionDto): void;
}

const DatabaseSelection: React.FC<DatabaseSelectionProps> = ({ DataSourceId, DatabaseReferenceId, DatabaseReferenceLabel, DatabaseTypeLabel, SetDatabaseSelected }) => {

    const position = { of: '#root' };

    const [refreshInProgress, setRefreshInProgress] = useState(false);

    const [dataSourceCharacteristics, setDataSourceCharacteristics] = useState<DataSourceCharacteristicsDto | null>(null);

    const dataSourceCharacteristicsLoad = useCallback(async () => {
        if (DataSourceId <= 0) {
            setDataSourceCharacteristics(new DataSourceCharacteristicsDto(false, false, false, false, false, false, false, false));
            return;
        }

        DataSourceCharacteristicsApi.GetDataSourceCharacteristicsByIdAsync(DataSourceId)
            .then(getDataSourceCharacteristicsResult => {
                setDataSourceCharacteristics(getDataSourceCharacteristicsResult.Result);
            });
    }, [DataSourceId]);

    useEffect(() => {
        dataSourceCharacteristicsLoad()
    }, [dataSourceCharacteristicsLoad]);

    const withCreate = dataSourceCharacteristics?.CreateSchemaAvailable ?? false;

    const databaseSelectionFormHandleRef = useRef<DatabaseSelectionFormHandle>(null);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success' | 'warning',
        message: string,
        withRedirect: boolean
    });

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

    }, [toastConfig]);

    const handleSetDatabaseSelected = (databaseSelectionDto: DatabaseSelectionDto) => {
        SetDatabaseSelected(databaseSelectionDto);
    };

    function selectDatabase(): void {
        if (databaseSelectionFormHandleRef.current !== null) {
            databaseSelectionFormHandleRef.current.Open(DatabaseReferenceId);
        }
    };

    const handleRefreshDatabasesAsync = async (e: ButtonTypes.ClickEvent) => {
        e.event?.stopPropagation();
        if (DataSourceId > 0) {
            setRefreshInProgress(true);
            let getDatabasesResult = await DatabaseApi.GetDatabasesAsync(DataSourceId, true);
            if (getDatabasesResult.IsFailed()) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: getDatabasesResult.ErrorMessage,
                    withRedirect: false
                });
                return;
            }

            if (getDatabasesResult.Result.Accepted === false) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshDatabasesNotAccepted'),
                    withRedirect: false
                });
                return;
            }

            if (getDatabasesResult.Result.Status === JobStatusError) {
                setRefreshInProgress(false);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'error',
                    message: formatMessage('RefreshDatabasesError'),
                    withRedirect: false
                });
                return;
            }

            setRefreshInProgress(false);
            setToastConfig({
                ...toastConfig,
                isVisible: true,
                type: 'success',
                message: formatMessage('RefreshDatabasesSuccess'),
                withRedirect: false
            });
        }
    };

    return (
        <React.Fragment>

            {DataSourceId <= 0 ?
                (<div style={{ width: 440, padding: '20px', opacity: 0.30, background: '#1A1A20', borderRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 32, display: 'inline-flex' }}>
                    <div style={{ flexDirection: 'column', justifyContent: 'left', alignItems: 'left', gap: 12, display: 'inline-flex' }}>
                        <div style={{ alignSelf: 'stretch', color: '#E3E3E8', fontSize: 16, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '24px', letterSpacing: 0.32, wordWrap: 'break-word' }}>2. Choose your database</div>
                        <div style={{ color: '#9393A2', fontSize: 12, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '15.60px', letterSpacing: 0.24, wordWrap: 'break-word' }}>No database selected</div>
                    </div>
                    <img src="svgs/plussource.svg" alt="plus" style={{ marginLeft: 'auto' }} />
                </div>) :
                (<div style={{ width: 440, padding: '20px', background: '#1A1A20', borderRadius: 16, border: '1px #3D3D46 solid', justifyContent: 'flex-start', alignItems: 'center', gap: '32px', display: 'inline-flex' }}>
                    <div style={{ flexDirection: 'column', justifyContent: 'left', alignItems: 'left', gap: 12, display: 'inline-flex' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ color: '#E3E3E8', fontSize: 16, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '24px', letterSpacing: 0.32, wordWrap: 'break-word' }}>2. Choose your {DatabaseTypeLabel}</div>
                            {DatabaseReferenceId > 0 && <img src="svgs/circle.svg" alt="circle" />}
                        </div>
                        <div style={{ color: '#9393A2', fontSize: 12, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '15.60px', letterSpacing: 0.24, wordWrap: 'break-word', alignSelf: 'flex-start' }}>
                            {DatabaseReferenceId > 0 ? DatabaseReferenceLabel + " selected" : "No " + DatabaseTypeLabel + " selected"}
                        </div>
                    </div>
                    {DatabaseReferenceId <= 0 ?
                        <img src="svgs/plussource.svg" alt="plus" style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={selectDatabase} /> :
                        <div style={{ marginLeft: 'auto', cursor: 'pointer', gap: 6, display: 'flex', alignItems: 'center' }} onClick={selectDatabase} >
                            <Button icon="refresh" style={{ marginRight: '5px' }} onClick={handleRefreshDatabasesAsync} />
                            <div style={{ color: '#E3E3E8', fontSize: 14, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '21px', letterSpacing: 0.28, wordWrap: 'break-word' }}>Edit</div>
                            <img src="svgs/edit.svg" alt="circle" />
                        </div>}
                </div>)}
            <DatabaseSelectionForm ref={databaseSelectionFormHandleRef} DataSourceId={DataSourceId} WithCreate={withCreate}SetDatabaseSelected={handleSetDatabaseSelected} />
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={refreshInProgress}
            />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={toastConfig.type === 'success' ? 1000 : 3000}
            />
        </React.Fragment>
    );
}

export default DatabaseSelection;